import { useState, useMemo } from 'react';
import { useAuthorisedFunction } from '@/utilities/authorised-function';
import GridLayout, { WidthProvider } from 'react-grid-layout';

import { Page } from '@/components/page';
import { Toggle } from '@/components/toggle';
import { GridPanel } from '@/components/grid-panel';
import { useBulkStats } from '@/hooks/stats/use-bulk-stats';
import { LineChartWidget } from '@/components/line-chart-widget';
import { ChartProvider } from '@/components/chart-provider';
import { ChainLogWidget } from '@/components/chain-log-widget';
import { ChainStatsWidget } from '@/components/chain-stats-widget';
import { useActiveAgents } from '@/features/agents/hooks/use-active-agents';
import { HorizontalBarChartWidget } from '@/components/horizontal-bar-chart-widget';
import { ObjectsTable } from '@/components/objects-table';
import { Widget } from '@/components/widget';

const layout = [
  { i: 'sidebar_1', x: 0, y: 0, w: 20, h: 11 },
  { i: 'sidebar_2', x: 20, y: 0, w: 20, h: 11 },
  { i: 'main', x: 0, y: 11, w: 40, h: 25 },
  { i: 'agent_runner_load', x: 0, y: 36, w: 40, h: 11 },
  { i: 'active_runs', x: 0, y: 47, w: 40, h: 15 },
];

const AutoWidthGridLayout = WidthProvider(GridLayout);

export function Monitor() {
  const [isDraggable, setIsDraggable] = useState<boolean>(false);
  const { isAuthorised } = useAuthorisedFunction();
  const isDeveloper = isAuthorised(['developer']);
  const { data: activeAgentsData, isLoading: activeAgentsLoading } = useActiveAgents({ enabled: isDeveloper });

  const activeRunsData = useMemo(
    () =>
      (activeAgentsData?.items ?? []).map((item) => ({
        uuid: item.uuid,
        name: item.agentTitle,
        data: item,
      })),
    [activeAgentsData?.items]
  );

  const activeRunsColumns = useMemo(
    () => [
      {
        header: 'Organisation',
        accessorKey: 'organisationName',
        cell: ({ row }: { row: any }) => (
          <span className="text-sm">{row.original.data?.organisationName}</span>
        ),
      },
      {
        header: 'Started',
        accessorKey: 'startedAt',
        cell: ({ row }: { row: any }) => {
          const d = row.original.data?.startedAt;
          return <span className="text-sm">{d ? new Date(d).toLocaleString() : ''}</span>;
        },
      },
      {
        header: 'PID',
        accessorKey: 'pid',
        cell: ({ row }: { row: any }) => (
          <span className="text-sm">{row.original.data?.pid}</span>
        ),
      },
      {
        header: 'Memory (MB)',
        accessorKey: 'memoryMb',
        cell: ({ row }: { row: any }) => (
          <span className="text-sm">
            {row.original.data?.memoryMb != null
              ? `${Math.round(row.original.data.memoryMb)} MB`
              : 'N/A'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Page
      title="Monitor"
      toolbar={
        <Toggle
          enabled={isDraggable}
          label="Enable drag"
          onChange={setIsDraggable}
        />
      }
    >
      <AutoWidthGridLayout
        className="layout"
        layout={layout}
        cols={40}
        rowHeight={30}
        isDraggable={isDraggable}
      >
        <GridPanel key="sidebar_1">
          <ChartProvider provider={useBulkStats}>
            <LineChartWidget
              title="Bulk Manager Load"
              xAxis={['-5', '-4', '-3', '-2', '-1', 'now']}
            />
          </ChartProvider>
        </GridPanel>
        <GridPanel key="sidebar_2">
          <ChainStatsWidget />
        </GridPanel>
        <GridPanel key="main">
          <ChainLogWidget />
        </GridPanel>
        {isDeveloper && (
          <GridPanel key="agent_runner_load">
            <ChartProvider
              provider={useActiveAgents}
              mapDataToChildProps={(d) => ({
                data: d?.series ?? [],
                max: d?.poolSize ?? undefined,
              })}
            >
              <HorizontalBarChartWidget title="Agent Runner Load" stackKeys={['jobs']} />
            </ChartProvider>
          </GridPanel>
        )}
        {isDeveloper && (
          <GridPanel key="active_runs">
            <Widget id="ACTIVE_RUNS_WIDGET" title="Active Agents">
              <ObjectsTable
                data={activeRunsData}
                isLoading={activeAgentsLoading}
                disableDrag
                customColumns={activeRunsColumns}
              />
            </Widget>
          </GridPanel>
        )}
      </AutoWidthGridLayout>
    </Page>
  );
}
